import api from './api';
import {
  extractSubscriptionFromUserPayload,
  normalizePlanLimits,
  normalizeSubscription,
} from '../utils/subscription';

/**
 * GET /api/plans — Bearer token added by api.js request interceptor.
 * @param {{ hideUnlimited?: boolean }} [options] Hide code "unlimited" on public/marketing pages
 * @returns {Promise<Array<object>>} Normalized plan list
 */
export const getPlans = async (options = {}) => {
  const response = await api.get('/plans');
  let plans = normalizePlansResponse(response.data);
  if (options.hideUnlimited) {
    plans = plans.filter((p) => p.code !== 'unlimited');
  }
  if (options.hideLegacy !== false) {
    plans = plans.filter((p) => !p.isLegacy);
  }
  return plans;
};

/**
 * GET /api/organization/subscription → data
 */
export const getOrganizationSubscription = async () => {
  const response = await api.get('/organization/subscription');
  const payload = response.data?.data ?? response.data;
  return normalizeSubscription(payload);
};

export { extractSubscriptionFromUserPayload };

/**
 * Accept common API shapes: [], { data: [] }, { plans: [] }.
 */
export function normalizePlansResponse(data) {
  if (!data) return [];
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data.plans)
      ? data.plans
      : Array.isArray(data.data)
        ? data.data
        : [];
  return list.map(normalizePlan).filter(Boolean);
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
}

function isExplicitlyFalseFlag(value) {
  return value === false || value === 0 || value === '0' || String(value).toLowerCase() === 'false';
}

/**
 * Pack is purchasable when API says so, or when priced + not explicitly disabled.
 * (Some catalog responses omit purchase_available even though checkout is live.)
 */
function resolvePurchaseAvailable(rawFlag, { hasPrice = false, parentAvailable = null } = {}) {
  if (isTruthyFlag(rawFlag)) return true;
  if (isExplicitlyFalseFlag(rawFlag)) return false;
  if (isTruthyFlag(parentAvailable)) return true;
  if (isExplicitlyFalseFlag(parentAvailable)) return false;
  return hasPrice;
}

function normalizeCreditPack(pack, parentPurchaseAvailable = null) {
  if (!pack || typeof pack !== 'object') return null;
  const envelopes = Number(pack.envelopes);
  const price = Number(pack.price);
  const amountCentsRaw = pack.amount_cents ?? pack.amountCents;
  let amountCents = Number(amountCentsRaw);
  if (!Number.isFinite(amountCents) && Number.isFinite(price)) {
    amountCents = Math.round(price * 100);
  }
  const hasPrice =
    (Number.isFinite(price) && price > 0)
    || (Number.isFinite(amountCents) && amountCents > 0);
  return {
    code: pack.code ?? null,
    envelopes: Number.isFinite(envelopes) ? envelopes : null,
    price: Number.isFinite(price) ? price : null,
    amountCents: Number.isFinite(amountCents) ? amountCents : null,
    currency: String(pack.currency || 'usd').toLowerCase(),
    label: pack.label ?? pack.name ?? pack.code ?? 'Credit pack',
    purchaseAvailable: resolvePurchaseAvailable(
      pack.purchase_available ?? pack.purchaseAvailable,
      { hasPrice, parentAvailable: parentPurchaseAvailable }
    ),
    raw: pack,
  };
}

export function normalizePlan(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const pricing = raw.pricing && typeof raw.pricing === 'object' ? raw.pricing : {};
  const stripeCheckout =
    raw.stripe_checkout && typeof raw.stripe_checkout === 'object'
      ? raw.stripe_checkout
      : {};
  const priceMonthly = pickPricingAmount(
    pricing.monthly,
    pricing.month,
    raw.price_monthly,
    raw.monthly_price,
    raw.prices?.monthly
  );
  const priceYearly = pickPricingAmount(
    pricing.annual,
    pricing.yearly,
    pricing.year,
    raw.price_yearly,
    raw.yearly_price,
    raw.annual_price,
    raw.prices?.annual
  );
  const annualSavings = pickPricingAmount(pricing.annual_savings, pricing.annualSavings);
  const genericPrice = raw.price ?? raw.amount ?? null;

  let features = raw.features ?? raw.feature_list ?? [];
  if (typeof features === 'string') {
    try {
      features = JSON.parse(features);
    } catch {
      features = features.split('\n').map((s) => s.trim()).filter(Boolean);
    }
  }
  if (!Array.isArray(features)) features = [];

  const limits = normalizePlanLimits(
    raw.limits ?? {
      envelopes_per_month: raw.envelopes_per_month ?? raw.envelope_limit,
      users_allowed: raw.users_allowed ?? raw.limits?.users,
    }
  );

  const parentPurchaseAvailableRaw = raw.purchase_available ?? raw.purchaseAvailable;
  const rawCreditPackList = Array.isArray(raw.credit_packs)
    ? raw.credit_packs
    : Array.isArray(raw.creditPacks)
      ? raw.creditPacks
      : [];
  const creditPacks = rawCreditPackList
    .map((pack) => normalizeCreditPack(pack, parentPurchaseAvailableRaw))
    .filter(Boolean);
  const codeKey = String(raw.code ?? raw.slug ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const nameKey = String(raw.name ?? '').trim().toLowerCase();
  const planKindRaw = String(raw.plan_kind ?? raw.planKind ?? '').toLowerCase();
  const planKind =
    planKindRaw === 'credit_pack'
    || codeKey === 'credit_pack'
    || codeKey.includes('credit_pack')
    || nameKey.includes('credit pack')
    || creditPacks.length > 0
      ? 'credit_pack'
      : 'subscription';
  const planPurchaseAvailable = resolvePurchaseAvailable(parentPurchaseAvailableRaw, {
    hasPrice: creditPacks.some((p) => p.purchaseAvailable || (p.price != null && p.price > 0)),
  });

  const intervalRaw = raw.interval ?? raw.billing_interval ?? raw.billing_period ?? null;
  const normalizedInterval =
    intervalRaw == null
      ? null
      : (() => {
          const interval = String(intervalRaw).toLowerCase();
          return interval === 'year' || interval === 'yearly' || interval === 'annual'
            ? 'year'
            : 'month';
        })();

  const priceForInterval =
    normalizedInterval === 'year'
      ? (priceYearly ?? genericPrice)
      : normalizedInterval === 'month'
        ? (priceMonthly ?? genericPrice)
        : (priceMonthly ?? genericPrice);

  const tagline = raw.tagline ?? raw.summary ?? '';
  const isLegacy =
    /legacy|superseded/i.test(String(raw.name || ''))
    || /superseded|legacy/i.test(String(raw.description || ''));

  const branding = raw.branding === true || raw.limits?.branding === true;

  return {
    id: raw.id ?? raw.plan_id,
    code: raw.code ?? raw.slug ?? null,
    slug: raw.slug ?? raw.code ?? null,
    name: raw.name ?? raw.title ?? raw.plan_name ?? 'Plan',
    planKind,
    tagline,
    description: raw.description ?? tagline ?? '',
    price: priceForInterval != null ? Number(priceForInterval) : null,
    priceMonthly: priceMonthly != null ? Number(priceMonthly) : null,
    priceYearly: priceYearly != null ? Number(priceYearly) : null,
    annualSavings: annualSavings != null ? Number(annualSavings) : null,
    currency: (pricing.currency || raw.currency || 'USD').toUpperCase(),
    interval: normalizedInterval,
    envelopeLimitLabel: raw.envelope_limit_label ?? null,
    branding,
    purchaseAvailable: planPurchaseAvailable || creditPacks.some((p) => p.purchaseAvailable),
    creditPacks,
    stripeCheckout: {
      monthlyAvailable: stripeCheckout.monthly_available === true,
      annualAvailable: stripeCheckout.annual_available === true,
      stripePriceIdMonthly: stripeCheckout.stripe_price_id_monthly ?? null,
      stripePriceIdAnnual: stripeCheckout.stripe_price_id_annual ?? null,
    },
    stripePriceId:
      stripeCheckout.stripe_price_id_monthly
      ?? raw.stripe_price_id
      ?? raw.stripePriceId
      ?? raw.stripe_price_id_monthly
      ?? null,
    stripePriceIdYearly:
      stripeCheckout.stripe_price_id_annual
      ?? raw.stripe_price_id_yearly
      ?? raw.stripe_price_id_annual
      ?? raw.yearly_stripe_price_id
      ?? null,
    features,
    limits,
    envelopesPerMonth: limits.envelopesPerMonth,
    usersAllowed: limits.usersAllowed,
    isCurrent: Boolean(raw.is_current ?? raw.current ?? raw.is_active_plan),
    isRecommended: Boolean(
      raw.is_recommended
      ?? raw.recommended
      ?? raw.popular
      ?? /most popular/i.test(tagline)
    ),
    isLegacy,
    // Credit packs use pack prices, not subscription monthly/annual.
    isContactSales:
      planKind !== 'credit_pack' && priceMonthly == null && priceYearly == null,
    raw,
  };
}

function pickPricingAmount(...candidates) {
  for (const value of candidates) {
    if (value === undefined || value === null || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}
